<template>
  <div class="gas-pressure-micro-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$P = F / A = ⅔ n Ēₖ$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>分子平均动能 (Ēₖ)</label>
            <span class="val">{{ (temp * 0.1).toFixed(1) }} <small>eV</small></span>
          </div>
          <div class="hud-item pink">
            <label>数密度 (n)</label>
            <span class="val">{{ (numMolecules / volume).toFixed(2) }} <small>nm⁻³</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" class="molecule-canvas">
          <!-- 容器器壁 (受压监测) -->
          <rect :x="100 - (volume * 0.8)" :y="40" :width="volume * 1.6" height="160" 
                fill="none" :stroke="hitColor" stroke-width="3" 
                :class="{ 'wall-hit': isHitting }" />
          
          <!-- 分子群体 -->
          <circle v-for="m in molecules" :key="m.id" 
                  :cx="m.x" :cy="m.y" :r="2.5" 
                  :fill="getMoleculeColor(m)" 
                  :opacity="0.9" />

          <!-- 压力表 (宏观表现) -->
          <g transform="translate(340, 120)">
            <circle r="35" fill="#1e293b" stroke="#334155" stroke-width="2" />
            <path d="M -25,0 A 25,25 0 0 1 25,0" fill="none" stroke="#475569" stroke-width="4" stroke-linecap="round" />
            <line x1="0" y1="0" :x2="28 * Math.cos(gaugeAngle)" :y2="28 * Math.sin(gaugeAngle)" 
                  stroke="#f43f5e" stroke-width="3" stroke-linecap="round" />
            <circle r="4" fill="#f43f5e" />
            <text y="22" text-anchor="middle" fill="#94a3b8" font-size="10" font-weight="700">PRESSURE</text>
          </g>
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
          <div class="control-item">
            <div class="label-row">
              <span class="label">热力学温度 (T)</span>
              <span class="val">{{ temp }} K</span>
            </div>
            <el-slider v-model="temp" :min="100" :max="1000" size="small"></el-slider>
          </div>
          <div class="control-item">
            <div class="label-row">
              <span class="label">容器体积 (V)</span>
              <span class="val">{{ (volume * 0.1).toFixed(1) }} L</span>
            </div>
            <el-slider v-model="volume" :min="40" :max="100" size="small"></el-slider>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
          <i class="el-icon-info"></i>
          <p><strong>物理详解：</strong>气压在微观上并非连续的力，而是大量分子与器壁发生弹性碰撞时产生的<strong>冲量叠加</strong>。通过实验可以看到：温度越高（动能越大）或体积越小（碰撞频率越高），宏观压力表显示的指数就越高。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GasPressureMicroViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      temp: 300,
      volume: 80,
      numMolecules: 50,
      molecules: [],
      isHitting: false,
      hitCount: 0,
      running: true
    }
  },
  computed: {
    pressure() {
      // P = nRT/V
      return (this.numMolecules * 0.01 * this.temp) / (this.volume * 0.1);
    },
    gaugeAngle() {
      const pNorm = Math.min(1, this.pressure / 300);
      return Math.PI - pNorm * Math.PI;
    },
    hitColor() {
      return this.isHitting ? '#f43f5e' : '#334155';
    }
  },
  methods: {
    initMolecules() {
      this.molecules = Array.from({ length: this.numMolecules }, (_, i) => ({
        id: i,
        x: 200 + (Math.random() - 0.5) * 50,
        y: 120 + (Math.random() - 0.5) * 50,
        vx: (Math.random() - 0.5) * 4,
        vy: (Math.random() - 0.5) * 4,
      }));
    },
    getMoleculeColor(m) {
      if (this.temp > 700) return '#fbbf24';
      if (this.temp < 300) return '#60a5fa';
      return '#22d3ee';
    },
    update() {
      if (!this.running) return;
      
      const speedScale = Math.sqrt(this.temp / 300);
      const halfW = this.volume * 0.8;
      const bounds = {
        xmin: 200 - halfW + 5,
        xmax: 200 + halfW - 5,
        ymin: 45,
        ymax: 195
      };

      let hitThisFrame = false;
      this.molecules.forEach(m => {
        m.x += m.vx * speedScale;
        m.y += m.vy * speedScale;

        if (m.x <= bounds.xmin || m.x >= bounds.xmax) {
          m.vx *= -1;
          hitThisFrame = true;
          // 防止穿透
          m.x = m.x <= bounds.xmin ? bounds.xmin + 1 : bounds.xmax - 1;
        }
        if (m.y <= bounds.ymin || m.y >= bounds.ymax) {
          m.vy *= -1;
          hitThisFrame = true;
          m.y = m.y <= bounds.ymin ? bounds.ymin + 1 : bounds.ymax - 1;
        }
      });

      if (hitThisFrame) {
        this.isHitting = true;
        clearTimeout(this.hitTimer);
        this.hitTimer = setTimeout(() => { this.isHitting = false; }, 50);
      }

      this.frameId = requestAnimationFrame(this.update);
    }
  },
  mounted() {
    this.initMolecules();
    this.update();
  },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
    clearTimeout(this.hitTimer);
  }
}
</script>

<style scoped lang="scss">
.gas-pressure-micro-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(30, 41, 59, 0.7); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f1f5f9; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #64748b; } }
      &.cyan { border-left: 3px solid #22d3ee; }
      &.pink { border-left: 3px solid #f43f5e; }
    }
  }
}

.wall-hit { transition: stroke 0.05s ease; stroke: #f43f5e !important; filter: drop-shadow(0 0 5px #ef4444); }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-item {
    .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } }
  }
}

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #2563eb; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
